import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Clock, ShieldCheck, ArrowRight, Building2 } from "lucide-react";
export default function PhaseIiiCareDelivery() {
  const localFeatures = [{
    icon: <Building2 className="size-5 text-primary" />,
    title: "Logistics Management",
    description: "Coordinating home equity extraction and aircraft leasebacks to fund care."
  }, {
    icon: <Clock className="size-5 text-primary" />,
    title: "Medical Coordination",
    description: "Liaising with specialized flight surgeons and in-home medical staff."
  }, {
    icon: <ShieldCheck className="size-5 text-primary" />,
    title: "Life Management",
    description: "Handling the daily checklist so you can focus on the right seat."
  }];
  return <section data-section-id="261150" id="phase-iii-care-delivery" className="py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {}
          <div className="flex flex-col items-start">
            <Badge variant="secondary" className="mb-6 bg-secondary text-secondary-foreground rounded-md">
              <MapPin className="mr-1.5 size-3.5" />
              Phase III: Care Delivery
            </Badge>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6 leading-tight tracking-tight">
              Command and Control: Your Dedicated Care Advisor
            </h2>
            
            <div className="space-y-5 text-lg text-muted-foreground mb-8 font-default">
              <p>
                In aviation, the transition from pilot-in-command to passenger is the most difficult maneuver. Our Care Delivery phase ensures you never lose authority over your life. Your dedicated Care Advisor acts as your mission commander, executing the flight plan we designed together.
              </p>
              <p>
                We don't just provide care; we manage the entire operation. From coordinating specialized medical support to managing the financial logistics of your aircraft and home equity, we maintain the standard of excellence you've lived by for decades.
              </p>
            </div>

            <div className="flex flex-wrap gap-4 mb-10">
              <Button asChild size="lg" className="font-semibold bg-primary text-primary-foreground hover:shadow-lg transition-all rounded-md">
                <Link to="/FlightDeckPage">Schedule a Free Consultation</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-secondary text-secondary-foreground hover:bg-secondary/10 rounded-md">
                <Link to="/TheFlightPlanPage">The Flight Plan</Link>
              </Button>
            </div>

            <div className="w-full pt-8 border-t border-border flex flex-col sm:flex-row gap-6 sm:gap-8">
              <Link to="/PilotTransitionsPage" className="group flex items-center text-sm font-medium text-primary hover:text-foreground transition-colors">
                Pilot Transitions
                <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/FAQPage" className="group flex items-center text-sm font-medium text-primary hover:text-foreground transition-colors">
                Operational FAQ
                <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div className="relative">
            <div className="relative rounded-md overflow-hidden shadow-2xl aspect-[4/3] lg:aspect-auto lg:h-[600px] border border-secondary/30">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/care_advisor_flight_charts.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/care_advisor_flight_charts.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/care_advisor_flight_charts.png-wvc-sizes" alt="Care Advisor reviewing flight charts with a pilot client" className="w-full h-full object-cover grayscale-[0.2] contrast-[1.1]" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent mix-blend-multiply" />
            </div>

            {}
            <Card className="relative mt-[-80px] mx-4 sm:mx-8 lg:absolute lg:bottom-8 lg:-left-12 lg:mx-0 lg:w-80 shadow-4xl border-border/50 bg-card/95 backdrop-blur-md rounded-md">
              <CardContent className="p-6">
                <h3 className="font-default font-bold text-lg text-foreground mb-4 border-b border-border pb-2 uppercase tracking-wider">
                  The Advisor Standard
                </h3>
                <ul className="space-y-4">
                  {localFeatures.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                      <div className="mt-0.5 shrink-0 bg-muted p-1.5 rounded-md border border-border">
                        {feature.icon}
                      </div>
                      <div>
                        <h4 className="text-sm font-bold font-default text-foreground uppercase tracking-tight">{feature.title}</h4>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed font-default">
                          {feature.description}
                        </p>
                      </div>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}